import {
  IDatasource, IGetRowsParams
} from "ag-grid-community";
import {Logger} from "@/util/logger";
import {BlobService} from "@/services/blob.service";
import {first} from "rxjs/operators";
import {errorAsText} from "@/util/util";
import {Blob as FileBlob, BlobType} from "@/models/blob";
import {DateTime} from "luxon";

/**
 *
 * @class FileBrowserDatasource
 *
 * @brief thea adaptor we use to fetch more rows when needed for virtual
 * scrolling of the file browser window.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2024
 *
 */
export class FileBrowserDatasource implements IDatasource {

  /** the total rows for the current directory */
  rowCount: number;

  /** the first page of rows */
  firstPage: any[] = [];

  /** the service we use to fetch contents of the directory being browsed */
  blobber: BlobService;

  /** the directory being browsed */
  path: string = '/';

  /** are we showing hidden files? */
  showHidden: boolean = false;

  /** standard constructor, intended to be seeded withe first few rows and the total row count */
  constructor(blobber: BlobService,
              path: string,
              showHidden: boolean,
              total: number,
              firstRows: any[]) {

    this.blobber    = blobber;
    this.path       = path;
    this.showHidden = showHidden;
    this.rowCount   = total;
    this.firstPage  = FileBrowserDatasource.render(path, firstRows, showHidden);

  }

  /** asynchronously fetch rows for AGGrid*/
  getRows(params: IGetRowsParams): void {

    let fbd: FileBrowserDatasource = this;

    /* if the range is within the first page, then just use what we were seeded with... */

    let useFirstPage: boolean = false;

    if (fbd.firstPage.length > 0) {

      useFirstPage = (params.startRow >= 0) && (params.endRow <= fbd.firstPage.length);

      if (fbd.rowCount < params.endRow) {

        /* AGGrid is trying to cache a block, but we don't even have enough for a full block */

        useFirstPage = true;
      }
    }

    if (useFirstPage) {

      /* we've already got the requested rows, hand them over ... */

      let page: any[] = fbd.firstPage.slice(params.startRow, params.endRow);

      Logger.debug(`[FileBrowserDatasource] return first page rows [${params.startRow}, ${params.endRow}]: `, page);

      setTimeout( () => {

        params.successCallback(page, fbd.rowCount);

        /*
         * clear the first page, so that we can't use it again...they may edit or delete rows
         * so we don't want to cache anything.   For the caching in AGGrid; we presume that
         * removing rows as part of deletion will invalidate the row's cache block.  So, we
         * don't have to explicitly worry about telling AGGrid when to not cache.
         *
         */

        fbd.firstPage = [];

      }, 100);

      return ;
    }

    /* do a normal fetch */

    let offset: number = params.startRow;
    let limit: number  = (params.endRow - params.startRow);

    Logger.debug(`[FileBrowserDatasource] fetching rows [${params.startRow}, ${params.endRow}], offset=${offset}, limit=${limit}...`);

    fbd.blobber.list(fbd.showHidden, fbd.path, '/', offset, limit)
      .pipe(first())
      .subscribe({

        next: (listing: any): void => {

          if (!Array.isArray(listing?.result)) {

            Logger.error(`[FileBrowserDatasource] garbled results from backend: `, listing);

            params.failCallback();

            return ;
          }

          /* render the raw rows */

          let page: any[] = FileBrowserDatasource.render(fbd.path, listing.result, fbd.showHidden);

          params.successCallback(page, fbd.rowCount);

        },

        error: (err): void => {

          Logger.error('[FileBrowserDatasource] could not fetch folder rows: ', errorAsText(err));

          params.failCallback();
        }

      });
  }

  /** helper to convert raw blob info objects into rows we can show in AGGrid */
  public static render(path: string, blobs: any[], showHidden: boolean = false): any[] {

    let rows: any[]                = [];

    /* walk the blobs and render ... */

    for (let item of blobs) {

      const name: string = item.name;
      const dir: string  = path.replace(new RegExp('/+$'), '');

      if (item.file === false) {

        /* it's a folder */

        rows.push({
          name,
          key:       dir + '/' + name,
          size:      item.size,
          date:      item.modified,
          type:      'folder',
          instances: 0,
          owner:     '',
          files:     item.files,
          icon:      '<img style="margin-right: 4px;" src="assets/images/blob-icons/blob-folder-20.png">'
        });

        continue;

      }

      /* it's a file, try to guess the type, so we can show a reasonable icon for the folder ... */

      let   icon: string;
      const type: BlobType = FileBlob.guessType(FileBlob.typeofString(item.type), item.key, item.name);

      if ((type !== BlobType.None) && (type !== BlobType.File)) {

        /* use the known content type */

        icon = '<img style="margin-right: 4px;" src="assets/images/blob-icons/' + FileBlob.iconOfType(type) + '">';

      } else {

        /* if we don't have a direct or extension based guessable type, it must be something else... */

        icon = '<img style="margin-right: 4px;" src="assets/images/blob-icons/' + FileBlob.iconOfType(BlobType.File) + '">';

        if (/\.json$/i.test(item.key) || /\.json$/i.test(item.name)) {

          icon = '<img style="margin-right: 4px;" src="assets/images/blob-icons/file-json-20.png">';

        }

        if (/\.csv$/i.test(item.key) || /\.csv$/i.test(item.name)) {

          icon = '<img style="margin-right: 4px;" src="assets/images/blob-icons/file-csv-20.png">';

        }
      }

      let modified: DateTime = DateTime.fromISO(item.modified.replace(' ', 'T'), {zone: 'UTC'}).toLocal();

      rows.push({
        name,
        key:       item.key,
        size:      item.size,
        date:      modified.toFormat(`yyyy-LL-dd HH:mm:ss`),
        type:      item.type,
        instances: item.instances,
        owner:     item.owner,
        files:     1,
        icon
      });

    } /* end of blob item walk */

    return rows;
  }
}
